/**
 * Created by Jonas Wålekvist on 2018-04-26.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Back, Linear, Power2, TimelineLite, TimelineMax, TweenLite} from "gsap"
import {DungeonQuestLayerConfig} from "../../../config/DungeonQuestLayerConfig";
import {MaskCreator} from "../../../utils/MaskCreator";
import {DungeonQuestHelper} from "../../bigSymbols/DungeonQuestHelper";
import {ShakeConfig, ShakeEffect} from "../../display/effects/ShakeEffect";
import {DungeonQuest} from "../../DungeonQuest";
import {DungeonQuestGameAssets} from "../../DungeonQuestGameAssets";
import {DungeonQuestGameConfig} from "../../DungeonQuestGameConfig";
import {DungeonQuestSound} from "../../DungeonQuestSound";


export class DungeonQuestRandomTransformIntroView extends BaseView {
    private _layer:ScalableLayer;
    private _character:PIXI.Sprite;
    private _plate:PIXI.Sprite;
    private _viewMask:PIXI.Graphics;
    private _characterWhiteOut1:PIXI.Sprite;
    private _characterWhiteOut2:PIXI.Sprite;
    private _whitePlate:PIXI.Graphics;
    private _plateContainer:PIXI.Container;
    private _introContainer:PIXI.Container;
    private _explosion:TimelineSprite;
    private _lightning:TimelineSprite;
    private _speedLines:TimelineSprite;
    private _speedLinesLoop:TimelineMax;
    private _symbolContainer:PIXI.Container;
    private _symbolLow:PIXI.Sprite;
    private _symbolMid:PIXI.Sprite;
    private _symbolLowWhite:PIXI.Sprite;
    private _symbolMidWhiteShine:PIXI.Sprite;
    private _symbolMidWhiteMask:PIXI.Graphics;
    private _symbolMidWhite:PIXI.Sprite;
    private _characterContainer:PIXI.Container;
    private _ring:PIXI.Sprite;
    private _symbolLayer:ScalableLayer;

    constructor(resourceGroup?:ResourcesGroupName) {
        super(resourceGroup);
        this._layer = StageManager.getLayer(DungeonQuestLayerConfig.RANDOM_FEATURE_INTRO.name);
        this._symbolLayer = StageManager.getLayer(DungeonQuestLayerConfig.SYMBOLS.name);
    }

    protected initAnimations():void {
        this._viewMask = MaskCreator.createSymbolAreaMaskGraphics();
        this._viewMask.pivot.set(this._viewMask.width * 0.5, 0);
        this._viewMask.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, 0);


        this._introContainer = new PIXI.Container();
        this._introContainer.position.set(0, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT * 0.4);
        this._introContainer.pivot.set(0, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT * 0.31);


        //************* CHARACTER
        this._characterContainer = new PIXI.Container();
        this._character = new PIXI.Sprite();
        this._character.anchor.set(0, 0);
        this._character.position.set(0, 0);

        this._characterWhiteOut1 = new PIXI.Sprite();
        this._characterWhiteOut1.anchor.set(0, 0);
        this._characterWhiteOut1.blendMode = PIXI.BLEND_MODES.ADD;

        this._characterWhiteOut2 = new PIXI.Sprite();
        this._characterWhiteOut2.anchor.set(0, 0);
        this._characterWhiteOut2.blendMode = PIXI.BLEND_MODES.ADD;

        this._characterContainer.addChild(this._characterWhiteOut2);
        this._characterContainer.addChild(this._characterWhiteOut1);
        this._characterContainer.addChild(this._character);


        //************* PLATE
        this._plateContainer = new PIXI.Container();
        this._plateContainer.position.set(0, 0);

        this._plate = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.getTransformIntroPlateName("L3"))[0]);
        this._plate.anchor.set(0, 0);

        this._whitePlate = new PIXI.Graphics();
        this._whitePlate.beginFill(0xffffff, 1);
        this._whitePlate.drawRect(-this._plate.width * 0.5, -this._plate.height * 0.5, this._plate.width, this._plate.height);
        this._whitePlate.position.set(this._plate.width * 0.5, this._plate.height * 0.5);

        this._speedLines = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.TRANSFORM_INTRO_SPEED_LINES));
        this._speedLines.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._speedLines.alpha = 0.5;
        this._speedLines.width = this._plate.width;
        this._speedLines.height = this._plate.height;
        this._speedLines.hide();

        this._lightning = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.TRANSFORM_INTRO_SYMBOL_LIGHTNING));
        this._lightning.blendMode = PIXI.BLEND_MODES.ADD;
        this._lightning.anchor.set(0.5, 0.5);
        this._lightning.position.set(this._plate.width * 0.5, this._plate.height * 0.5);
        this._lightning.hide();


        this._plateContainer.addChild(this._plate);
        this._plateContainer.addChild(this._speedLines);
        this._plateContainer.addChild(this._whitePlate);
        this._plateContainer.addChild(this._lightning);
        this._plateContainer.position.set((720 - this._plate.width) * 0.5, 0);

        this._ring = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.TRANSFORM_INTRO_RING)[0]);
        this._ring.anchor.set(0.5, 0.5);
        this._ring.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT * 0.5 + 150);
        this._ring.alpha = 0;


        //************* SYMBOLS
        this._symbolContainer = this.createSymbolStuff();
        this._symbolContainer.position.set(360, 435);


        this._introContainer.addChild(this._plateContainer);
        this._introContainer.addChild(this._characterContainer);
        this._introContainer.addChild(this._symbolContainer);


        this._symbolLayer.addChild(this._viewMask);
        this.addChild(this._ring);
        this.addChild(this._introContainer);

        this.mask = this._viewMask;
    }


    protected onResize(resizeData:IResizeData):void {

        if(resizeData.orientation == Orientation.PORTRAIT) {
            this._viewMask.width = Math.ceil(resizeData.width / this._symbolLayer.scale.x);
        } else {
            this._viewMask.scale.x = 1;
        }
    }

    private setUpgradeTextures(symbol:string) {
        if(symbol == "") {
            this._plate.texture = PIXI.Texture.EMPTY;
            this._character.texture = PIXI.Texture.EMPTY;
        } else {
            this._plate.texture = GameResources.getTextures(DungeonQuestGameAssets.getTransformIntroPlateName(symbol))[0];
            this._character.texture = GameResources.getTextures(DungeonQuestGameAssets.getTransformIntroGfxName(symbol))[0];
            this._characterWhiteOut1.texture = DungeonQuest.colorEffectTextureCache.getWhiteTexture(GameResources.getTextures(DungeonQuestGameAssets.getTransformIntroGfxName(symbol))[0]);
            this._characterWhiteOut2.texture = DungeonQuest.colorEffectTextureCache.getWhiteTexture(GameResources.getTextures(DungeonQuestGameAssets.getTransformIntroGfxName(symbol))[0]);

            this._character.alpha = 1;
            this._characterWhiteOut1.alpha = 0.8;
            this._characterWhiteOut2.alpha = 0.5;
            this._plate.alpha = 0;
        }
    }

    private resetIntro(symbol:string, firstCPoint:PIXI.Point) {
        this._characterContainer.alpha = 1;
        this._character.position.set(firstCPoint.x, firstCPoint.y);
        this._characterWhiteOut1.position.set(firstCPoint.x, firstCPoint.y);
        this._characterWhiteOut2.position.set(firstCPoint.x, firstCPoint.y);
        this._plate.alpha = 0;
        this._whitePlate.alpha = 1;
        this._whitePlate.height = 3;
        this._whitePlate.width = 0;
        this._lightning.tint = DungeonQuestHelper.getSymbolColor(symbol);


        this.setUpgradeTextures(symbol);
        this.resetSymbolAnimation(symbol);

    }

    public getIntroAnimation(symbol:string):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const cPoints = DungeonQuestRandomTransformIntroView.getCharacterPoints(symbol);

        // const customEase = CustomEase.create("custom", "M0,0 C0.084,0.61 0.074,0.854 0.14,0.908 0.216,0.97 0.374,1 1,1");
        //const customEase = CustomEase.create("custom", "M0,0 C0.084,0.61 0.096,0.326 0.162,0.38 0.205,0.415 0.151,0.706 0.318,0.87 0.439,0.989 0.735,1 1,1");
        const customEase = CustomEase.create("custom", "M0,0 C0.02,0.634 0.067,0.872 0.134,0.926 0.212,0.988 0.735,1 1,1");

        tl.add(() => this._layer.addChild(this));

        this.resetIntro(symbol, cPoints[0]);


        tl.add([
            new TweenLite(this._whitePlate, 0.3, {width : this._plate.width}),
            DungeonQuest.reelAreaEffectController.fadeInDarkenPlate(0.3)
        ]);
        tl.add([
            new TweenLite(this._whitePlate, 0.3, {height : this._plate.height, ease : Back.easeOut}),

        ]);
        tl.add(() => this._plate.alpha = 1);
        tl.add([
            new TweenLite(this._whitePlate, 0.3, {alpha : 0}),
            () => this.startSpeedLineLoop()
        ]);

        const fadeOffset:number = 0.7;
        const charMoveIn = new TimelineLite();
        charMoveIn.add(new TweenLite(this._character.position, 0.7, {
            x : cPoints[1].x,
            y : cPoints[1].y,
            ease : customEase
        }));
        charMoveIn.add(new TweenLite(this._characterWhiteOut1.position, 0.8, {
            x : cPoints[1].x,
            y : cPoints[1].y,
            ease : customEase
        }), 0.05);
        charMoveIn.add(new TweenLite(this._characterWhiteOut2.position, 0.9, {
            x : cPoints[1].x,
            y : cPoints[1].y,
            ease : customEase
        }), 0.1);
        charMoveIn.add([
            new TweenLite(this._characterWhiteOut1, fadeOffset, {alpha : 0, ease : Power2.easeOut}),
            new TweenLite(this._characterWhiteOut2, fadeOffset, {alpha : 0, ease : Power2.easeOut})
        ], `-=${fadeOffset}`);

        tl.add([
            this._lightning.getAnimationAutoShowHide(),
            charMoveIn
        ], tl.totalDuration() - 0.4, "start", 0.3);


        tl.add(this.getSymbolAnimation(), "-=0.3");


        const startEndTime = tl.totalDuration();
        tl.add([
            new TweenLite(this._whitePlate, 0.3, {alpha : 1}),
            new TweenLite(this._characterContainer, 0.3, {alpha : 0}),
            DungeonQuest.reelAreaEffectController.fadeOutDarkenPlate(0.3)
        ]);
        tl.add(() => this.stopSpeedLineLoop());
        tl.add(() => this._plate.alpha = 0);
        tl.add([
            new TweenLite(this._whitePlate, 0.15, {height : 2, ease : Back.easeIn})
        ]);
        tl.add([
            new TweenLite(this._whitePlate, 0.15, {width : 0}),
        ]);

        tl.add(this.getSymbolEndAnimation(), startEndTime);
        tl.add(this.getRingAnimation(), "-=0.3");

        tl.add(() => this._layer.removeChild(this));
        return tl;

    }

    private static getCharacterPoints(symbol:string):PIXI.Point[] {
        let points:PIXI.Point[] = [];
        if(symbol == "L1") {
            points = [
                new PIXI.Point(-720, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(720, 2),
            ];
        } else if(symbol == "L2") {
            points = [
                new PIXI.Point(720, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(-720, 2),
            ];
        } else if(symbol == "L3") {
            points = [
                new PIXI.Point(720, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(-720, 2),
            ];
        } else {
            points = [
                new PIXI.Point(-720, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(0, 2),
                new PIXI.Point(720, 2),
            ];
        }
        return points;
    }

    private startSpeedLineLoop() {
        if(this._speedLinesLoop && this._speedLinesLoop.isActive()) {
            this.stopSpeedLineLoop();
        }
        this._speedLines.show();
        this._speedLinesLoop = new TimelineMax({repeat : -1});
        this._speedLinesLoop.add(this._speedLines.getAnimation());

    }

    private stopSpeedLineLoop() {
        if(this._speedLinesLoop) {
            this._speedLinesLoop.pause();
            this._speedLines.hide();
        }
    }

    private resetSymbolAnimation(symbol:string) {
        const symbolNumber = symbol.substring(1);

        const lowTexture = DungeonQuestGameAssets.getSymbolStateTextures("L" + symbolNumber)[0];
        this._symbolLow.texture = lowTexture;
        this._symbolLowWhite.texture = DungeonQuest.colorEffectTextureCache.getWhiteTexture(lowTexture);

        const midTexture = DungeonQuestGameAssets.getSymbolStateTextures("M" + symbolNumber)[0];
        this._symbolMid.texture = midTexture;
        this._symbolMidWhite.texture = DungeonQuest.colorEffectTextureCache.getWhiteTexture(midTexture);
        this._symbolMidWhiteShine.texture = DungeonQuest.colorEffectTextureCache.getWhiteTexture(midTexture);

        this._explosion.tint = DungeonQuestHelper.getSymbolColor(symbol);

        this._symbolLow.scale.set(0);
        this._symbolLow.alpha = 1;
        this._symbolLowWhite.alpha = 0;
        this._symbolMid.alpha = 0;
        this._symbolMid.scale.set(0.9);
        this._symbolMidWhite.alpha = 1;
        this._symbolMidWhiteMask.y = -170;
    }

    private getSymbolAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        const shake = new ShakeEffect([this._symbolLow], new ShakeConfig(3));
        const lowEnterEase = CustomEase.create("lowEnter", "M0,0 C0,0 0.14,1.526 0.358,1.814 0.419,1.895 0.838,1.83 0.846,1.782 0.876,1.586 1,1 1,1");

        const shineEase = CustomEase.create("custom", "M0,0 C0.034,0.158 0.301,0.747 0.47,0.884 0.586,0.978 0.818,1 1,1");

        tl.add(() => SlotGame.sound.playEffect(DungeonQuestSound.RANDOM_TRANSFORM_SYMBOL));
        tl.add(new TweenLite(this._symbolLow.scale, 0.5, {x : 1, y : 1, ease : lowEnterEase}));
        tl.add(new TweenLite(this._symbolLow.scale, 0.2, {x : 1.4, y : 1.4}));
        tl.add([
            shake.getOneShotShake(0.3),
            new TweenLite(this._symbolLowWhite, 0.3, {alpha : 1}),
        ]);

        const explosionStart = tl.totalDuration() - 0.12;

        tl.add([
            () => this._symbolMid.alpha = 1,
            () => this._symbolLow.alpha = 0
        ]);
        tl.add([
            new TweenLite(this._symbolMid.scale, 0.5, {x : 1, y : 1, ease : Back.easeOut.config(3)}),
            new TweenLite(this._symbolMidWhite, 0.4, {alpha : 0, delay : 0.1})
        ]);

        tl.add(this._explosion.getAnimationAutoShowHide(), explosionStart);

        tl.add(new TweenLite(this._symbolMidWhiteMask.position, 0.75, {y : 170, ease : shineEase}));

        return tl;

    }

    private getSymbolEndAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            new TweenLite(this._symbolMid.scale, 0.3, {x : 1.3, y : 1.3, ease : Back.easeIn}),
            new TweenLite(this._symbolMidWhite, 0.3, {alpha : 1})
        ]);
        tl.add(() => this._symbolMid.texture = PIXI.Texture.EMPTY);
        tl.add([
            new TweenLite(this._symbolMid.scale, 0.3, {x : 1.0, y : 1.0}),
            new TweenLite(this._symbolMid, 0.3, {alpha : 0})
        ]);

        return tl;
    }

    private getRingAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            () => this._ring.alpha = 1,
            () => this._ring.scale.set(0.8, 0.8)
        ]);

        tl.add(new TweenLite(this._ring.scale, 0.3, {x : 4.5, y : 4.5, ease : Linear.easeNone}));
        tl.add([
            () => this._ring.alpha = 0,
            () => this._ring.scale.set(0, 0)
        ]);

        return tl;
    }

    private createSymbolStuff():PIXI.Container {
        const container:PIXI.Container = new PIXI.Container();

        const symbolScaleOffsetContainer = new PIXI.Container();
        symbolScaleOffsetContainer.scale.set(1.65);

        const lowTexture = DungeonQuestGameAssets.getSymbolStateTextures("L3")[0];
        this._symbolLow = new PIXI.Sprite(lowTexture);
        this._symbolLow.anchor.set(0.5, 0.5);

        this._symbolLowWhite = new PIXI.Sprite(DungeonQuest.colorEffectTextureCache.getWhiteTexture(lowTexture));
        this._symbolLowWhite.anchor.set(0.5, 0.5);
        this._symbolLow.addChild(this._symbolLowWhite);


        const midTexture = DungeonQuestGameAssets.getSymbolStateTextures("M3")[0];
        this._symbolMid = new PIXI.Sprite(midTexture);
        this._symbolMid.anchor.set(0.5, 0.5);

        this._symbolMidWhite = new PIXI.Sprite(DungeonQuest.colorEffectTextureCache.getWhiteTexture(midTexture));
        this._symbolMidWhite.anchor.set(0.5, 0.5);
        this._symbolMid.addChild(this._symbolMidWhite);

        this._symbolMidWhiteShine = new PIXI.Sprite(DungeonQuest.colorEffectTextureCache.getWhiteTexture(midTexture));
        this._symbolMidWhiteShine.anchor.set(0.5, 0.5);

        this._symbolMidWhiteMask = new PIXI.Graphics();
        this._symbolMidWhiteMask.beginFill(0x000000, 1);
        this._symbolMidWhiteMask.drawRect(-200, -20, 400, 40);
        this._symbolMidWhiteMask.position.set(0, 0);
        this._symbolMidWhiteMask.rotation = 1;

        this._symbolMidWhiteShine.addChild(this._symbolMidWhiteMask);
        this._symbolMidWhiteShine.mask = this._symbolMidWhiteMask;

        this._explosion = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.TRANSFORM_INTRO_SYMBOL_EXPLOSION));
        this._explosion.blendMode = PIXI.BLEND_MODES.ADD;
        this._explosion.anchor.set(0.5, 0.5);
        this._explosion.hide();


        symbolScaleOffsetContainer.addChild(this._symbolLow);
        symbolScaleOffsetContainer.addChild(this._symbolMid);
        symbolScaleOffsetContainer.addChild(this._symbolMidWhiteShine);
        container.addChild(symbolScaleOffsetContainer);
        container.addChild(this._explosion);

        return container;
    }

}